/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  INDIA — OPERATOR & COMPLIANCE CONFIGURATION
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *  Every value in this file appears verbatim in a legal notice shown to Indian
 *  patients. They are NOT cosmetic. Each one must be confirmed by the operating
 *  entity and its counsel before go-live.
 *
 *  Statutory sources these values feed:
 *    • Digital Personal Data Protection Act, 2023 ("DPDP Act") + DPDP Rules,
 *      2025 (notified 13 Nov 2025; substantive obligations live 13 May 2027)
 *      — s.5 notice, s.6 consent, s.8(9) contact, Rule 3 itemised notice.
 *    • Telemedicine Practice Guidelines, 2020 ("TPG 2020") — Appendix 5 to the
 *      IMC (Professional Conduct, Etiquette and Ethics) Regulations, 2002.
 *    • ICMR Ethical Guidelines for AI in Biomedical Research & Healthcare, 2023.
 *    • Consumer Protection (E-Commerce) Rules, 2020 — grievance officer.
 *
 *  ⚠ FIELDS MARKED `TODO_BEFORE_GO_LIVE` ARE PLACEHOLDERS. Shipping them as-is
 *    publishes an inaccurate legal notice.
 */

export const TODO_BEFORE_GO_LIVE = '⚠ TO BE CONFIRMED BY OPERATOR';

/** The entity that is the "Data Fiduciary" under DPDP s.2(i). */
export interface OperatorIdentity {
  legalName: string;
  cin: string;
  registeredAddress: string;
  /**
   * Clinical Establishments (Registration and Regulation) Act, 2010
   * registration — required in adopting States/UTs.
   */
  clinicalEstablishmentRegNo: string;
  supportEmail: string;
  supportPhone: string;
}

/**
 * DPDP s.8(9) + Rule 3(b): the notice MUST carry the business contact of the
 * Data Protection Officer (or the person able to answer questions about
 * processing), and a link to withdraw consent / exercise rights / complain.
 */
export interface DataProtectionContacts {
  dpoName: string;
  dpoEmail: string;
  dpoPhone: string;
  /** Consumer Protection (E-Commerce) Rules, 2020 r.4(5) — 48h ack, 1 month resolution. */
  grievanceOfficerName: string;
  grievanceOfficerEmail: string;
  grievanceRedressalUrl: string;
  /** Where a Data Principal escalates if we do not resolve. */
  dataProtectionBoardUrl: string;
}

/**
 * Where Indian patients' health data physically comes to rest.
 *
 * ⚠ FINDING (2026-08): this application authenticates against AWS Cognito in
 *   `eu-west-1` (Ireland) and calls an API in the same region. Indian patient
 *   health data therefore leaves India.
 *
 *   Under DPDP s.16 that is presently PERMITTED — India uses a negative-list
 *   model, and no restricting notification covers Ireland. It is NOT, however,
 *   silent-able: s.5 requires the transfer to be disclosed in the notice, which
 *   is why this object exists and is rendered to the patient.
 *
 *   Two caveats the operator must resolve separately:
 *     1. RBI payment-data localisation (DPSS.CO.OD.No.2785/06.08.005/2017-18)
 *        requires end-to-end payment data to be stored only in India. A non-India
 *        card processor for Indian patients is a live non-compliance.
 *     2. If the platform ever becomes an ABDM Health Information User, the
 *        ABDM Health Data Management Policy expects India-resident storage.
 */
export interface DataResidency {
  primaryRegion: string;
  primaryRegionLabel: string;
  countriesOfProcessing: string[];
  transferSafeguard: string;
}

/**
 * TPG 2020 applies to a "Registered Medical Practitioner" — a doctor enrolled
 * in the State or National Medical Register under the NMC Act, 2019.
 *
 * ⚠ FINDING: a physician licensed only outside India is NOT an RMP. Under
 *   s.34 NMC Act, 2019 practising medicine in India without registration is an
 *   offence, and TPG 2020 confers no authority on a foreign-registered doctor
 *   to diagnose or prescribe for a patient in India.
 *
 *   If overseas specialists review Indian cases, the lawful posture is that the
 *   overseas output is a NON-PRESCRIPTIVE academic opinion delivered to, and
 *   countersigned by, an India-registered RMP who owns the clinical advice.
 *   Set `overseasReviewersInvolved` accurately — it changes the consent text
 *   the patient is shown, and misdeclaring it is the single largest regulatory
 *   exposure in this product.
 */
export interface ClinicalGovernance {
  overseasReviewersInvolved: boolean;
  /** The India-registered RMP accountable for opinions released to patients. */
  responsibleRmpName: string;
  responsibleRmpRegNo: string;
  responsibleRmpStateCouncil: string;
}

export const OPERATOR: OperatorIdentity = {
  legalName: TODO_BEFORE_GO_LIVE,
  cin: TODO_BEFORE_GO_LIVE,
  registeredAddress: TODO_BEFORE_GO_LIVE,
  clinicalEstablishmentRegNo: TODO_BEFORE_GO_LIVE,
  supportEmail: TODO_BEFORE_GO_LIVE,
  supportPhone: TODO_BEFORE_GO_LIVE,
};

export const DP_CONTACTS: DataProtectionContacts = {
  dpoName: TODO_BEFORE_GO_LIVE,
  dpoEmail: TODO_BEFORE_GO_LIVE,
  dpoPhone: TODO_BEFORE_GO_LIVE,
  grievanceOfficerName: TODO_BEFORE_GO_LIVE,
  grievanceOfficerEmail: TODO_BEFORE_GO_LIVE,
  grievanceRedressalUrl: '/help',
  dataProtectionBoardUrl: 'https://www.meity.gov.in/data-protection-framework',
};

export const DATA_RESIDENCY: DataResidency = {
  primaryRegion: 'eu-west-1',
  primaryRegionLabel: 'Ireland (European Union)',
  countriesOfProcessing: ['Ireland (European Union)'],
  transferSafeguard:
    'Contractual confidentiality and security obligations with our hosting and ' +
    'processing providers, encryption in transit and at rest, and access limited ' +
    'to personnel and clinicians who need it to deliver your second opinion.',
};

export const CLINICAL_GOVERNANCE: ClinicalGovernance = {
  overseasReviewersInvolved: true,
  responsibleRmpName: TODO_BEFORE_GO_LIVE,
  responsibleRmpRegNo: TODO_BEFORE_GO_LIVE,
  responsibleRmpStateCouncil: TODO_BEFORE_GO_LIVE,
};

/**
 * Retention.
 *
 * Two clocks run at once and the LONGER one governs:
 *   • DPDP s.8(7) + Rule 8 — erase once the purpose is served.
 *   • IMC Regulations 2002, Reg. 1.3.1 + Appendix 3 — retain medical records
 *     for 3 years from commencement of treatment; TPG 2020 carries this into
 *     telemedicine records. Limitation and consumer-forum exposure in practice
 *     pushes clinical establishments beyond this.
 */
export const RETENTION = {
  medicalRecordYears: 3,
  medicalRecordBasis:
    'Regulation 1.3.1 of the Indian Medical Council (Professional Conduct, ' +
    'Etiquette and Ethics) Regulations, 2002, read with the Telemedicine ' +
    'Practice Guidelines, 2020.',
  /** Reg. 1.3.2 — records must be furnished to the patient within 72 hours. */
  recordRequestTurnaroundHours: 72,
  /** DPDP Rules 2025 — Data Principal requests answered within 90 days. */
  rightsRequestMaxDays: 90,
} as const;

/** Age of majority for consent purposes — DPDP s.2(f) defines a child as <18. */
export const AGE_OF_MAJORITY = 18;
